/**
 * 동기화 큐 관리
 */

import { getRecord, putRecord, deleteRecord, getAllRecords, clearStore } from "./index";
import type { SyncQueueItem, ItemPatch } from "./types";

const STORE_NAME = "syncQueue";

/**
 * 동기화 큐 ID 생성
 */
function generateSyncId(): string {
  return `sync-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * 같은 문서의 기존 pending 항목 찾기
 */
async function findExistingPendingItem(
  type: SyncQueueItem["type"],
  projectId?: string,
  docKey?: string,
  machineId?: string
): Promise<SyncQueueItem | undefined> {
  if (!projectId || !docKey || !machineId) return undefined;

  const items = await getAllRecords<SyncQueueItem>(STORE_NAME);
  return items.find(
    (item) =>
      item.type === type &&
      item.projectId === projectId &&
      item.docKey === docKey &&
      item.machineId === machineId &&
      (!item.status || item.status === "pending")
  );
}

/**
 * 같은 measureType + measureIndex의 기존 pending 항목 찾기
 * measure coalescing 전용 (measureType과 measureIndex까지 필터링)
 */
async function findExistingPendingMeasureItem(
  projectId?: string,
  docKey?: string,
  machineId?: string,
  measureType?: string,
  measureIndex?: number
): Promise<SyncQueueItem | undefined> {
  if (!projectId || !docKey || !machineId) return undefined;

  const items = await getAllRecords<SyncQueueItem>(STORE_NAME);
  return items.find(
    (item) =>
      item.type === "measure" &&
      item.operation === "update" &&
      item.projectId === projectId &&
      item.docKey === docKey &&
      item.machineId === machineId &&
      item.payload.measureType === measureType &&
      item.payload.measureIndex === measureIndex &&
      (!item.status || item.status === "pending")
  );
}

/**
 * 패치 병합 (같은 itemId + field는 덮어쓰기)
 */
function mergePatches(existing: ItemPatch[], incoming: ItemPatch[]): ItemPatch[] {
  const merged = [...existing];

  for (const patch of incoming) {
    const existingIndex = merged.findIndex(
      (p) => p.itemId === patch.itemId && p.field === patch.field
    );
    if (existingIndex >= 0) {
      // 같은 항목+필드 → 값 덮어쓰기
      merged[existingIndex] = patch;
    } else {
      // 새 패치 추가
      merged.push(patch);
    }
  }

  return merged;
}

/**
 * MachineInfo 패치 병합 (같은 field는 덮어쓰기)
 */
function mergeMachineInfoPatches(
  existing: Array<{ field: string; value: unknown }>,
  incoming: Array<{ field: string; value: unknown }>
): Array<{ field: string; value: unknown }> {
  const merged = [...existing];

  for (const patch of incoming) {
    const existingIndex = merged.findIndex((p) => p.field === patch.field);
    if (existingIndex >= 0) {
      merged[existingIndex] = patch;
    } else {
      merged.push(patch);
    }
  }

  return merged;
}

/**
 * 동기화 큐에 항목 추가 (같은 문서는 패치 병합)
 */
export async function addToSyncQueue(
  item: Omit<SyncQueueItem, "id" | "createdAt" | "retryCount" | "status" | "lastError">
): Promise<string> {
  // checklist, data, machine 타입은 패치 병합 시도
  if (item.type === "checklist" || item.type === "data" || item.type === "machine") {
    const existing = await findExistingPendingItem(
      item.type,
      item.projectId,
      item.docKey,
      item.machineId
    );

    if (existing) {
      // 기존 항목에 패치 병합
      const incomingPatches = item.payload.patches as ItemPatch[] | undefined;
      const incomingMachinePatches = item.payload.machineInfoPatches as
        | Array<{ field: string; value: unknown }>
        | undefined;

      const updatedPayload = { ...existing.payload };

      if (incomingPatches && incomingPatches.length > 0) {
        const existingPatches = (existing.payload.patches as ItemPatch[]) || [];
        updatedPayload.patches = mergePatches(existingPatches, incomingPatches);
      }

      if (incomingMachinePatches && incomingMachinePatches.length > 0) {
        const existingMachinePatches =
          (existing.payload.machineInfoPatches as Array<{ field: string; value: unknown }>) || [];
        updatedPayload.machineInfoPatches = mergeMachineInfoPatches(
          existingMachinePatches,
          incomingMachinePatches
        );
      }

      await putRecord(STORE_NAME, {
        ...existing,
        payload: updatedPayload,
        // expectedVersion은 최신 값 유지
        expectedVersion: item.expectedVersion ?? existing.expectedVersion,
      });

      return existing.id;
    }
  }

  // measure update: 같은 measureType + measureIndex 조합은 데이터 덮어쓰기 (coalescing)
  if (item.type === "measure" && item.operation === "update") {
    const existing = await findExistingPendingMeasureItem(
      item.projectId,
      item.docKey,
      item.machineId,
      item.payload.measureType as string,
      item.payload.measureIndex as number
    );

    if (existing) {
      // 같은 계측 항목에 대한 update → measureData를 머지
      // measureOldData는 최초 이전값을 유지하되, 새 필드의 이전값은 추가
      const existingOldData =
        (existing.payload.measureOldData as Record<string, unknown> | undefined) ?? {};
      const incomingOldData =
        (item.payload.measureOldData as Record<string, unknown> | undefined) ?? {};
      const mergedOldData: Record<string, unknown> = { ...existingOldData };
      for (const key of Object.keys(incomingOldData)) {
        if (!(key in mergedOldData)) {
          mergedOldData[key] = incomingOldData[key];
        }
      }

      const updatedPayload = {
        ...existing.payload,
        measureData: {
          ...(existing.payload.measureData as Record<string, unknown>),
          ...(item.payload.measureData as Record<string, unknown>),
        },
        measureOldData: mergedOldData,
      };

      await putRecord(STORE_NAME, {
        ...existing,
        payload: updatedPayload,
        expectedVersion: item.expectedVersion ?? existing.expectedVersion,
      });

      return existing.id;
    }
  }

  // 새 항목 생성
  const id = generateSyncId();
  const queueItem: SyncQueueItem = {
    ...item,
    id,
    createdAt: Date.now(),
    retryCount: 0,
    status: "pending",
  };
  await putRecord(STORE_NAME, queueItem);
  return id;
}

/**
 * 계측 삭제 시 관련 measurePhoto 사진 큐 항목 정리 (#458)
 * - 삭제된 인덱스와 동일한 사진: 큐에서 제거
 * - 삭제된 인덱스보다 큰 사진: measureIndex -1 보정
 *
 * @param items 검색 대상 큐 항목 배열 (전체 큐 또는 그룹 내 항목)
 * @param deletedMeasureType 삭제된 계측 타입
 * @param deletedIndex 삭제된 계측 인덱스
 */
export async function adjustMeasurePhotoQueue(
  items: SyncQueueItem[],
  deletedMeasureType: string,
  deletedIndex: number
): Promise<void> {
  for (const item of items) {
    const payload = item.payload as Record<string, unknown>;
    if (
      item.type === "photo" &&
      payload.uploadType === "measurePhoto" &&
      payload.measureType === deletedMeasureType &&
      (!item.status || item.status === "pending") &&
      typeof payload.measureIndex === "number"
    ) {
      const idx = payload.measureIndex as number;
      if (idx === deletedIndex) {
        await removeSyncQueueItem(item.id);
      } else if (idx > deletedIndex) {
        payload.measureIndex = idx - 1;
        await updateSyncQueueItem(item.id, { payload: item.payload });
      }
    }
  }
}

/**
 * 모든 동기화 큐 항목 조회
 */
export async function getSyncQueue(): Promise<SyncQueueItem[]> {
  const items = await getAllRecords<SyncQueueItem>(STORE_NAME);

  // 정렬 우선순위: equipment create > measure create > 나머지 (시간순)
  // update/delete는 사용자 조작 순서(createdAt)를 유지해야 인덱스 정합성 보장
  return items.sort((a, b) => {
    // equipment create를 최우선 (앞으로)
    const aIsEquipmentCreate = a.type === "equipment" && a.operation === "create";
    const bIsEquipmentCreate = b.type === "equipment" && b.operation === "create";

    if (aIsEquipmentCreate && !bIsEquipmentCreate) return -1;
    if (!aIsEquipmentCreate && bIsEquipmentCreate) return 1;

    // measure create를 우선 (서버에 항목이 존재해야 update/delete 가능)
    const aIsMeasureCreate = a.type === "measure" && a.operation === "create";
    const bIsMeasureCreate = b.type === "measure" && b.operation === "create";

    if (aIsMeasureCreate && !bIsMeasureCreate) return -1;
    if (!aIsMeasureCreate && bIsMeasureCreate) return 1;

    // equipment/delete는 최후순위 (관련 항목이 먼저 처리되도록) (#457)
    const aIsEquipmentDelete = a.type === "equipment" && a.operation === "delete";
    const bIsEquipmentDelete = b.type === "equipment" && b.operation === "delete";
    if (aIsEquipmentDelete && !bIsEquipmentDelete) return 1;
    if (!aIsEquipmentDelete && bIsEquipmentDelete) return -1;

    // 나머지는 생성 시간순 (사용자 조작 순서 유지)
    return a.createdAt - b.createdAt;
  });
}

/**
 * 특정 동기화 항목 조회
 */
export async function getSyncQueueItem(id: string): Promise<SyncQueueItem | undefined> {
  return getRecord<SyncQueueItem>(STORE_NAME, id);
}

/**
 * 동기화 항목 업데이트
 */
export async function updateSyncQueueItem(
  id: string,
  updates: Partial<SyncQueueItem>
): Promise<void> {
  const existing = await getSyncQueueItem(id);
  if (existing) {
    await putRecord(STORE_NAME, { ...existing, ...updates });
  }
}

/**
 * 동기화 항목 삭제
 */
export async function removeSyncQueueItem(id: string): Promise<void> {
  await deleteRecord(STORE_NAME, id);
}

/**
 * 모든 동기화 큐 삭제
 */
export async function clearSyncQueue(): Promise<void> {
  await clearStore(STORE_NAME);
}

/**
 * 대기 중인 동기화 항목 수
 */
export async function getPendingSyncCount(): Promise<number> {
  const items = await getSyncQueue();
  // status가 없거나 "pending"인 항목 카운트 (하위 호환성)
  return items.filter((item) => !item.status || item.status === "pending").length;
}

/**
 * 재시도 횟수 증가
 */
export async function incrementRetryCount(id: string): Promise<void> {
  const item = await getSyncQueueItem(id);
  if (item) {
    await putRecord(STORE_NAME, {
      ...item,
      retryCount: item.retryCount + 1,
    });
  }
}

/**
 * 실패한 동기화 항목 조회
 */
export async function getFailedSyncItems(): Promise<SyncQueueItem[]> {
  const items = await getSyncQueue();
  return items.filter((item) => item.status === "failed");
}

/**
 * 완료된 동기화 항목 조회
 */
export async function getCompletedSyncItems(): Promise<SyncQueueItem[]> {
  const items = await getSyncQueue();
  return items.filter((item) => item.status === "completed");
}

/**
 * 오래된 완료 항목 자동 정리
 * @param maxAgeMs 최대 보관 기간 (기본: 72시간)
 * @returns 삭제된 항목 수
 */
export async function cleanupCompletedItems(
  maxAgeMs: number = 72 * 60 * 60 * 1000
): Promise<number> {
  const items = await getSyncQueue();
  const now = Date.now();
  let deletedCount = 0;

  for (const item of items) {
    if (item.status === "completed" && item.syncedAt && now - item.syncedAt > maxAgeMs) {
      await deleteRecord(STORE_NAME, item.id);
      deletedCount++;
    }
  }

  return deletedCount;
}

/**
 * 설비 삭제 시 해당 설비의 모든 pending syncQueue 항목 일괄 제거 (#457)
 * measure, checklist, photo 등 해당 machineId의 모든 pending 항목을 제거한다.
 */
export async function cleanupEquipmentQueue(
  machineId: string,
  projectId: string,
  docKey: string
): Promise<number> {
  const queue = await getSyncQueue();
  const targets = queue.filter(
    (item) =>
      item.machineId === machineId &&
      item.projectId === projectId &&
      item.docKey === docKey &&
      (!item.status || item.status === "pending")
  );

  for (const item of targets) {
    await removeSyncQueueItem(item.id);
  }

  return targets.length;
}

/**
 * 오프라인 생성→즉시 삭제 시 equipment/create 큐 상쇄 (#457)
 * @returns true이면 상쇄됨 (delete 큐 등록 불필요), false이면 일반 삭제 진행
 */
export async function cancelEquipmentCreate(
  machineId: string,
  projectId: string,
  docKey: string
): Promise<boolean> {
  const queue = await getSyncQueue();
  const createItem = queue.find(
    (item) =>
      item.type === "equipment" &&
      item.operation === "create" &&
      (!item.status || item.status === "pending") &&
      ((item.createdMachineIds && item.createdMachineIds.includes(machineId)) ||
        item.machineId === machineId) &&
      item.projectId === projectId &&
      item.docKey === docKey
  );

  if (createItem) {
    // create 항목에서 해당 machineId만 제거하거나 전체 제거
    if (createItem.createdMachineIds && createItem.createdMachineIds.length > 1) {
      // 여러 설비를 한번에 생성한 경우 해당 ID만 제거하고 count도 동기화
      const updatedIds = createItem.createdMachineIds.filter((id) => id !== machineId);
      const currentCount =
        (createItem.payload.count as number) || createItem.createdMachineIds.length;
      await updateSyncQueueItem(createItem.id, {
        createdMachineIds: updatedIds,
        payload: {
          ...createItem.payload,
          count: currentCount - 1,
        },
      });
    } else {
      await removeSyncQueueItem(createItem.id);
    }
    return true;
  }
  return false;
}

/**
 * 같은 문서의 나머지 큐 항목들의 expectedVersion 업데이트
 * 동기화 성공 후 버전 충돌 방지용
 */
export async function updateQueueVersionForDocument(
  projectId: string,
  docKey: string,
  machineId: string,
  newVersion: number,
  excludeItemId?: string
): Promise<number> {
  const items = await getSyncQueue();
  let updatedCount = 0;

  for (const item of items) {
    // 같은 문서의 항목만 업데이트 (현재 동기화된 항목 제외)
    if (
      item.projectId === projectId &&
      item.docKey === docKey &&
      item.machineId === machineId &&
      item.id !== excludeItemId &&
      (!item.status || item.status === "pending")
    ) {
      await updateSyncQueueItem(item.id, { expectedVersion: newVersion });
      updatedCount++;
    }
  }

  return updatedCount;
}
